import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../redux";
import { View, Text, Button, SettingsList, SettingsListItem } from "../../components/base";
import { PageLayout } from "../../components/PageLayout";
import { Icon } from "../../components/base/Icon";
import { AgentEnvironmentDropdownField } from "../../components/AgentEnvironmentDropdownField";
import { DropdownField } from "../../components/base/DropdownField";
import { InputField } from "../../components/base/InputField";
import { Dialog } from "../../components/base/Dialog";
import { useApiRequest } from "../../hooks/useApiRequest";
import { SimulationAgent, simulationAgentActions } from "../../redux/reducers/simulationAgentSlice";
import { agentActions } from "../../redux/reducers/agentSlice";
import { agentEnvironmentActions } from "../../redux/reducers/agentEnvironmentSlice";

export const AgentSimulationsPage = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const agentEnvironments = useAppSelector((state) => Object.values(state.agentEnvironment.entities).filter((env) => env.agentId === agentId));
  const simulationAgents = useAppSelector((state) => Object.values(state.simulationAgent.entities));
  const simulationAgentsIsLoading = useAppSelector((state) => state.simulationAgent.isLoading);
  const agents = useAppSelector((state) => Object.values(state.agent.entities));
  const agentsIsLoading = useAppSelector((state) => state.agent.isLoading);

  const defaultEnvironmentId = useMemo(() => (agentEnvironments.length > 0 ? agentEnvironments[0].id : ""), [agentEnvironments]);
  const [agentEnvironmentId, setAgentEnvironmentId] = useState<string>(defaultEnvironmentId);
  const [selectedAgentId, setSelectedAgentId] = useState<string>(agentId || "");

  // Get environments for the selected agent in simulation config
  const selectedAgentEnvironments = useAppSelector((state) => Object.values(state.agentEnvironment.entities).filter((env) => env.agentId === selectedAgentId));
  const selectedAgentDefaultEnvironmentId = useMemo(
    () => (selectedAgentEnvironments.length > 0 ? selectedAgentEnvironments[0].id : ""),
    [selectedAgentEnvironments],
  );
  const [lastRunSimulationAgentId, setLastRunSimulationAgentId] = useState<string | null>(null);
  const [isSimulationConfigDialogOpen, setIsSimulationConfigDialogOpen] = useState(false);
  const [selectedSimulationAgent, setSelectedSimulationAgent] = useState<SimulationAgent | null>(null);
  const [numberOfSimulations, setNumberOfSimulations] = useState<number>(1);
  const [simulationEnvironmentId, setSimulationEnvironmentId] = useState<string>(defaultEnvironmentId);

  useEffect(() => {
    if (!agentEnvironmentId && defaultEnvironmentId) {
      setAgentEnvironmentId(defaultEnvironmentId);
    }
  }, [defaultEnvironmentId, agentEnvironmentId]);

  useEffect(() => {
    if (!simulationEnvironmentId && defaultEnvironmentId) {
      setSimulationEnvironmentId(defaultEnvironmentId);
    }
  }, [defaultEnvironmentId, simulationEnvironmentId]);

  // Load environments when selected agent changes
  useEffect(() => {
    if (selectedAgentId) {
      dispatch(agentEnvironmentActions.get({ agentId: selectedAgentId }));
    }
  }, [selectedAgentId, dispatch]);

  // Update environment when selected agent changes
  useEffect(() => {
    if (selectedAgentDefaultEnvironmentId) {
      setSimulationEnvironmentId(selectedAgentDefaultEnvironmentId);
    }
  }, [selectedAgentId, selectedAgentDefaultEnvironmentId]);

  useEffect(() => {
    dispatch(agentActions.get({}));
    dispatch(simulationAgentActions.get({}));
  }, []);

  const runSimulationAgentRequest = useApiRequest<{ data: { id: string } }>();

  const openSimulationConfigDialog = (simulationAgent: SimulationAgent) => {
    setSelectedSimulationAgent(simulationAgent);
    setNumberOfSimulations(1);
    setSelectedAgentId(agentId || "");
    setSimulationEnvironmentId(defaultEnvironmentId);
    setIsSimulationConfigDialogOpen(true);
  };

  const closeSimulationConfigDialog = () => {
    setIsSimulationConfigDialogOpen(false);
    setSelectedSimulationAgent(null);
    setNumberOfSimulations(1);
    setSelectedAgentId(agentId || "");
  };

  const onRunSimulationAgent = (simulationAgent: SimulationAgent) => {
    openSimulationConfigDialog(simulationAgent);
  };

  const handleRunSimulation = async () => {
    if (!selectedSimulationAgent || !selectedAgentId) {
      return;
    }

    setLastRunSimulationAgentId(selectedSimulationAgent.id);

    const { error: runError } = await runSimulationAgentRequest.fetch(`/v1/simulationAgents/${selectedSimulationAgent.id}/run`, {
      method: "POST",
      body: {
        numberOfSimulations,
        agentId: selectedAgentId,
        agentEnvironmentId: simulationEnvironmentId,
      },
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!runError) {
      setLastRunSimulationAgentId(selectedSimulationAgent.id);
      closeSimulationConfigDialog();
    }
  };

  return (
    <>
      <PageLayout
        title="Simulations"
        description="Create and manage simulation agents to test different prompts and behaviors in safe environments."
      >
        <View className="flex flex-col gap-4 mt-8">
          <View className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <Text className="text-lg font-semibold">Simulation Agents</Text>
            <Button
              onClick={() => navigate("/agents/simulations/new")}
              className="btn-primary"
            >
              <Icon
                name="add"
                size={18}
                className="mr-2"
              />
              New Simulation Agent
            </Button>
          </View>

          {simulationAgentsIsLoading ? (
            <View className="flex items-center justify-center py-8">
              <Text className="text-sm text-gray-500 dark:text-gray-400">Loading simulation agents...</Text>
            </View>
          ) : simulationAgents.length === 0 ? (
            <View className="flex flex-col items-center justify-center gap-2 py-8 rounded-lg border border-dashed border-gray-300 dark:border-gray-700">
              <Icon
                name="users"
                size={32}
                className="text-indigo-500"
              />
              <Text className="text-sm text-gray-500 dark:text-gray-400 text-center max-w-md">
                Create a simulation agent to test different prompts and behaviors without impacting live environments.
              </Text>
            </View>
          ) : (
            <SettingsList>
              {simulationAgents.map((simulationAgent) => {
                const isCurrent = lastRunSimulationAgentId === simulationAgent.id;
                const isRunning = runSimulationAgentRequest.isLoading && isCurrent;
                const showSuccess = isCurrent && runSimulationAgentRequest.data?.data?.id && !runSimulationAgentRequest.error;
                const showError = isCurrent && runSimulationAgentRequest.error;

                const icon = (simulationAgent.name?.trim()?.[0] || "S").toUpperCase();

                return (
                  <SettingsListItem
                    key={simulationAgent.id}
                    icon={icon}
                    title={simulationAgent.name}
                    subtitle={
                      <View className="flex flex-col gap-1 min-w-0">
                        <Text className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-line max-h-40 overflow-y-auto">
                          {simulationAgent.systemPrompt || "No system prompt provided."}
                        </Text>
                        <Text className="text-xs text-gray-400 dark:text-gray-500">Updated {new Date(simulationAgent.updatedAt).toLocaleString()}</Text>
                      </View>
                    }
                    right={
                      <View className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/agents/simulations/${simulationAgent.id}`)}
                        >
                          <Icon
                            name="edit"
                            size={16}
                          />
                        </Button>
                        <Button
                          onClick={() => onRunSimulationAgent(simulationAgent)}
                          isLoading={isRunning}
                          className="btn-primary"
                        >
                          <Icon
                            name="play"
                            size={18}
                            className="mr-2"
                          />
                          Run
                        </Button>
                      </View>
                    }
                    below={
                      <>
                        {showSuccess && (
                          <View className="rounded-md border border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20 p-3">
                            <Text className="text-green-800 dark:text-green-300">
                              Simulation started. Session ID: {runSimulationAgentRequest.data?.data?.id}
                            </Text>
                          </View>
                        )}

                        {showError && (
                          <View className="rounded-md border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 p-3">
                            <Text className="text-red-700 dark:text-red-300">
                              {runSimulationAgentRequest.error?.message || "Failed to run simulation agent"}
                            </Text>
                          </View>
                        )}
                      </>
                    }
                  />
                );
              })}
            </SettingsList>
          )}
        </View>
      </PageLayout>

      <Dialog
        isOpen={isSimulationConfigDialogOpen}
        onClose={closeSimulationConfigDialog}
        title="Run Simulations"
        size="md"
        actions={
          <>
            <Button
              variant="ghost"
              onClick={closeSimulationConfigDialog}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRunSimulation}
              isDisabled={
                !selectedSimulationAgent || !selectedAgentId || numberOfSimulations < 1 || !simulationEnvironmentId || runSimulationAgentRequest.isLoading
              }
              isLoading={runSimulationAgentRequest.isLoading}
              className="btn-primary"
            >
              Start
            </Button>
          </>
        }
      >
        <View className="space-y-4 p-4">
          {selectedSimulationAgent && (
            <View className="mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <Text className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 mr-1">Simulation Agent</Text>
              <Text className="text-base font-semibold text-gray-900 dark:text-white">{selectedSimulationAgent?.name}</Text>
            </View>
          )}

          <InputField
            label="Number of Simulations"
            placeholder="Enter number of simulations to run"
            value={numberOfSimulations.toString()}
            onChange={(value) => setNumberOfSimulations(Math.max(1, parseInt(value) || 1))}
            type="number"
          />
          <DropdownField
            label="Agent"
            options={agents.map((agent) => ({
              value: agent.id,
              label: agent.name,
            }))}
            value={selectedAgentId}
            onChange={setSelectedAgentId}
            placeholder="Select an agent"
            readOnly={agentsIsLoading}
          />
          <AgentEnvironmentDropdownField
            label="Environment"
            agentEnvironments={selectedAgentEnvironments}
            value={simulationEnvironmentId}
            onChange={setSimulationEnvironmentId}
          />
        </View>
      </Dialog>
    </>
  );
};
